import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowLeft, CreditCard, Calendar, CheckCircle, Tag } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { ClipLoader } from 'react-spinners';
import { formatCurrency } from '../utils/formatters';

function CreditCardDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  useEffect(() => {
    fetchInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedMonth, selectedYear]);

  const fetchInvoiceDetails = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/credit-cards/${id}/invoices?month=${selectedMonth}&year=${selectedYear}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Erro ao buscar fatura do cartão.');
      setInvoice(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const formatMonthYear = (month, year) => {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase());
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button 
          onClick={() => navigate('/app/credit-cards')} 
          style={{ 
            padding: '8px', 
            background: 'var(--bg-secondary)', 
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-primary)'
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <header className="main-header" style={{ marginBottom: 0, flex: 1 }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CreditCard size={28} /> 
            {invoice ? invoice.card : 'Detalhes do Cartão'}
          </h2>
          <p>Visualize e gerencie as faturas do seu cartão</p>
        </header>
      </div>

      <div className="card filter-section" style={{ justifyContent: 'space-between' }}>
        <button onClick={handlePrevMonth} style={{ padding: '8px', background: 'transparent' }}>
          <ChevronLeft size={24} />
        </button>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0 }}>
          Fatura de {formatMonthYear(selectedMonth, selectedYear)}
        </h2>
        <button onClick={handleNextMonth} style={{ padding: '8px', background: 'transparent' }}>
          <ChevronRight size={24} />
        </button>
      </div>

      {isLoading ? (
        <div className="loading-spinner-container" style={{ minHeight: '60vh' }}>
          <ClipLoader color="var(--accent-primary)" size={60} />
        </div>
      ) : !invoice ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <CreditCard size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <p style={{ color: 'var(--text-secondary)' }}>Cartão não encontrado.</p>
        </div>
      ) : (
        <div className="stats-grid" style={{ gridTemplateColumns: '1fr 2fr', alignItems: 'start' }}>
          {/* Card Resumo da Fatura */}
          <div className="card" style={{ 
            background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary, #4f46e5) 100%)',
            color: 'white',
            padding: '24px'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '500', opacity: 0.9, marginBottom: '8px' }}>
              Total da Fatura
            </h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '24px' }}>
              {formatCurrency(invoice.total_amount || 0)}
            </p>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '16px', 
              padding: '16px',
              backdropFilter: 'blur(10px)'
            }}>
              <div className="budget-tracker-item" style={{ marginBottom: '12px' }}>
                <div className="budget-tracker-header">
                  <span className="budget-tracker-category" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} /> Período
                  </span>
                  <span className="budget-tracker-values" style={{ color: 'white' }}>
                    {formatDate(invoice.start_date)} a {formatDate(invoice.end_date)}
                  </span>
                </div>
              </div>
              {invoice.closing_day && (
                <div className="budget-tracker-item">
                  <div className="budget-tracker-header">
                    <span className="budget-tracker-category" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Fechamento / Vencimento
                    </span>
                    <span className="budget-tracker-values" style={{ color: 'white' }}>
                      Dia {invoice.closing_day} / Dia {invoice.due_day}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lista de Transações */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 'bold' }}>Lançamentos</h3>
            </div>
            {invoice.transactions?.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <CheckCircle size={48} style={{ margin: '0 auto 16px', opacity: 0.5, color: 'var(--accent-success, #10b981)' }} />
                <p style={{ color: 'var(--text-secondary)' }}>Nenhuma compra registrada nesta fatura.</p>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {invoice.transactions?.map((t) => (
                  <li key={t.id} style={{ 
                    padding: '16px 24px', 
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background 0.2s'
                  }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '999px',
                        background: 'var(--accent-primary)',
                        opacity: 0.1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent-primary)'
                      }}>
                        <CreditCard size={20} />
                      </div>
                      <div>
                        <p style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {t.title}
                          {t.total_installments && t.total_installments > 1 && (
                            <span style={{ 
                              marginLeft: '8px', 
                              fontSize: '0.75rem', 
                              fontWeight: 'normal',
                              background: 'var(--bg-tertiary)',
                              padding: '2px 8px',
                              borderRadius: '999px'
                            }}>
                              {t.installment_number}/{t.total_installments}
                            </span>
                          )}
                        </p>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{formatDate(t.date)}</span>
                          {t.category && (
                            <>
                              <span style={{ width: '4px', height: '4px', borderRadius: '999px', background: 'var(--text-secondary)' }}></span>
                              <Tag size={12} /> {t.category}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(t.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        button {
          cursor: pointer;
          transition: all 0.2s;
        }
        button:hover {
          opacity: 0.8;
        }
        ul li:hover {
          background: var(--bg-tertiary);
        }
      `}</style>
    </>
  );
}

export default CreditCardDetailsPage;